import config from '@app/config';
import { getDisplayUrl, isValidUrl } from '@app/helpers/url';

export enum LinkItemType {
    url,
    title,
    description,
    emoji,
    order,
}

export interface LinkItemError {
    field?: LinkItemType;
    message: string;
}

export interface LinkItemFields {
    url: string;
    title: string;
    description?: string;
    emoji?: string;
    order: number;
}

const DEFAULT_EMOJI = '🔗';
export class LinkItem {
    hash = '';
    fields: LinkItemFields = { order: 0, url: '', title: '', emoji: DEFAULT_EMOJI, description: '' };
    dirty = false;
    ignoreReorder = false;

    constructor(linkItem?: LinkItem | string, hash?: string) {
        if (hash) {
            this.hash = hash;
        }
        if (linkItem) {
            if (typeof linkItem === 'string') {
                try {
                    const items = JSON.parse(linkItem);
                    Object.keys(items).forEach((key) => {
                        this.fields[key] = items[key];
                    });
                } catch (_) {
                    //It's not json yet.
                    const items = linkItem.split('|');

                    if (items.length !== 5) {
                        return;
                    }

                    this.fields = {
                        url: items[LinkItemType.url],
                        title: items[LinkItemType.title],
                        description: items[LinkItemType.description],
                        emoji: items[LinkItemType.emoji],
                        order: parseInt(items[LinkItemType.order]),
                    };
                }
            } else {
                this.setFromLinkItem(linkItem);
            }
        }
        this.dirty = false;
        return new Proxy(this, {
            get: function (classProxy, field) {
                if (field in classProxy) {
                    return classProxy[field];
                }
                if (classProxy.fields && field in classProxy.fields) {
                    return classProxy.fields[field];
                }
                return classProxy[field];
            },
            set: function (classProxy, field, value) {
                const oldValue = classProxy.fields[field] || classProxy[field];
                if (classProxy.fields && field in classProxy.fields) {
                    classProxy.fields[field] = value;
                } else {
                    classProxy[field] = value;
                }

                if (field !== 'dirty' && field !== 'ignoreReorder') {
                    classProxy.dirty = oldValue !== value;
                }

                return true;
            },
        });
    }

    setFromLinkItem(linkItem: LinkItem): void {
        this.fields = { ...linkItem.fields };
        this.hash = linkItem.hash;
    }

    dataFieldsEmpty(): boolean {
        return this.fields.title === '' && this.fields.description === '' && this.fields.url === '';
    }

    isEmpty(): boolean {
        return this.dataFieldsEmpty() && (this.hash === '' || this.hash.indexOf('temp') === 0);
    }
    set title(val: string) {
        this.fields.title = val;
    }

    get title(): string {
        return this.fields.title;
    }

    set description(val: string) {
        this.fields.description = val;
    }

    get description(): string {
        return this.fields.description || '';
    }

    get order(): number {
        return this.fields.order;
    }

    set order(val: number) {
        this.fields.order = val;
    }
    //decoded emoji (for the picker and display)
    get emoji(): string {
        return decodeURI(this.fields.emoji || '');
    }

    //set emoji (encoded for the api)
    set emoji(val: string) {
        this.fields.emoji = encodeURIComponent(val || DEFAULT_EMOJI);
    }

    /**
     * Display url that is decoded and excludes the https?://
     */
    get displayUrl(): string {
        return decodeURI(getDisplayUrl(this.fields.url).displayUrl);
    }

    /**
     * Edit url that is decoded and includes the https?://
     */
    get editUrl(): string {
        const displayUrl = getDisplayUrl(this.fields.url);
        return decodeURI(displayUrl.url);
    }

    /**
     * Raw url that is encoded with the https?:// used for <a> tags
     */
    get url(): string {
        const displayUrl = getDisplayUrl(this.fields.url);
        if (!displayUrl.displayUrl.trim()) {
            return '';
        }
        return displayUrl.url || '';
    }

    set decodedUrl(decodedVal: string) {
        this.fields.url = encodeURI(decodedVal);
    }

    /**
     * Returns false if there is no error, otherwise
     */
    hasError(singleField?: LinkItemType): Array<LinkItemError> {
        const errors: LinkItemError[] = [];
        if (this.convertToDelimitedLinkTag().length > config.linkItemMaxLength) {
            errors.push({
                field: undefined,
                message: `Length cannot be greater than ${config.linkItemMaxLength} characters`,
            });
        }
        if (!isValidUrl(this.url)) {
            errors.push({ message: 'Url is invalid', field: LinkItemType.url });
        }
        if (this.fields.title === '') {
            errors.push({ message: 'Title cannot be empty', field: LinkItemType.title });
        }
        if (singleField) {
            return errors.filter((e) => e.field === singleField);
        }
        return errors;
    }

    convertToDelimitedLinkTag(): string {
        return JSON.stringify(this.toJson());
        // return `${this.url}|${this.fields.title}|${this.fields.description}|${this.fields.emoji}|${this.fields.order}`;
    }

    toJson(): LinkItemFields {
        return this.fields;
    }
}
